import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

export interface MetricConfig {
  id: string;
  label: string;
  category: "kpi" | "financial" | "classes" | "visualization";
  enabled: boolean;
  order: number;
}

export interface MetricsConfig {
  metrics: MetricConfig[];
}

const DEFAULT_METRICS: MetricConfig[] = [
  // KPIs principales (activados por defecto)
  { id: "totalUsers", label: "Total Usuarios", category: "kpi", enabled: true, order: 0 },
  { id: "monthlyRevenue", label: "Ingresos del Mes", category: "kpi", enabled: true, order: 1 },
  { id: "attendanceRate", label: "Tasa de Asistencia", category: "kpi", enabled: true, order: 2 },
  { id: "completedRoutines", label: "Rutinas Completadas", category: "kpi", enabled: true, order: 3 },
  
  // KPIs adicionales
  { id: "retentionRate", label: "Tasa de Retención", category: "kpi", enabled: false, order: 4 },
  { id: "averageTicket", label: "Ticket Promedio", category: "kpi", enabled: false, order: 5 },
  { id: "newUsersMonth", label: "Usuarios Nuevos", category: "kpi", enabled: false, order: 6 },
  { id: "penalizedUsers", label: "Usuarios Penalizados", category: "kpi", enabled: false, order: 7 },
  
  // Métricas financieras - KPIs
  { id: "totalRevenue", label: "Ingresos Totales", category: "financial", enabled: false, order: 8 },
  { id: "maxPayment", label: "Pago Más Alto", category: "financial", enabled: false, order: 9 },
  { id: "revenuePerUser", label: "Ingreso por Usuario", category: "financial", enabled: false, order: 10 },
  { id: "delinquencyRate", label: "Tasa de Morosidad", category: "financial", enabled: false, order: 11 },
  { id: "monthlyProjection", label: "Proyección del Mes", category: "financial", enabled: false, order: 12 },
  { id: "recoverableRevenue", label: "Ingresos Recuperables", category: "financial", enabled: false, order: 13 },
  { id: "previousMonthRevenue", label: "Mes Anterior", category: "financial", enabled: true, order: 14 },
  { id: "revenueGrowth", label: "Crecimiento Ingresos", category: "financial", enabled: false, order: 15 },
  
  // Métricas de clases
  { id: "totalClasses", label: "Total Clases", category: "classes", enabled: true, order: 16 },
  { id: "classOccupancy", label: "Ocupación de Clases", category: "classes", enabled: false, order: 17 },
  { id: "averageAttendance", label: "Asistencia Promedio", category: "classes", enabled: true, order: 18 },
  
  // Visualizaciones
  { id: "revenueChart", label: "Gráfico de Ingresos", category: "visualization", enabled: true, order: 19 },
  { id: "financialMetrics", label: "Métricas Financieras", category: "visualization", enabled: true, order: 20 },
  { id: "paymentDistribution", label: "Distribución de Pagos", category: "visualization", enabled: false, order: 21 },
  { id: "revenueByPlan", label: "Ingresos por Plan", category: "visualization", enabled: false, order: 22 },
  { id: "expiringMemberships", label: "Membresías por Vencer", category: "visualization", enabled: true, order: 23 },
  { id: "popularClasses", label: "Clases Populares", category: "visualization", enabled: true, order: 24 },
  { id: "userGrowthChart", label: "Crecimiento de Usuarios", category: "visualization", enabled: false, order: 25 },
  { id: "classOccupancyCard", label: "Ocupación de Clases", category: "visualization", enabled: false, order: 26 },
];

const STORAGE_KEY = "gym-metrics-config";

export const useGymMetricsConfig = (gymId: string) => {
  const [metrics, setMetrics] = useState<MetricConfig[]>(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY}-${gymId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new metrics added after initial save
        return DEFAULT_METRICS.map((defaultMetric, index) => {
          const savedMetric = parsed.find((m: MetricConfig) => m.id === defaultMetric.id);
          return savedMetric 
            ? { ...defaultMetric, enabled: savedMetric.enabled, order: savedMetric.order ?? index } 
            : { ...defaultMetric, order: defaultMetric.order ?? index };
        }).sort((a, b) => a.order - b.order);
      } catch {
        return DEFAULT_METRICS;
      }
    }
    return DEFAULT_METRICS;
  });

  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-${gymId}`, JSON.stringify(metrics));
  }, [metrics, gymId]);

  const toggleMetric = (metricId: string) => {
    setMetrics(prev =>
      prev.map(m =>
        m.id === metricId ? { ...m, enabled: !m.enabled } : m
      )
    );
  };

  const setMetricEnabled = (metricId: string, enabled: boolean) => {
    setMetrics(prev =>
      prev.map(m =>
        m.id === metricId ? { ...m, enabled } : m
      )
    );
  };

  const reorderMetrics = useCallback((sourceIndex: number, destinationIndex: number, category?: MetricConfig["category"]) => {
    setMetrics(prev => {
      const allMetrics = [...prev];
      
      if (category) {
        // Get enabled metrics in this category, sorted by order
        const categoryEnabledMetrics = allMetrics
          .filter(m => m.category === category && m.enabled)
          .sort((a, b) => a.order - b.order);
        
        if (sourceIndex >= categoryEnabledMetrics.length || destinationIndex >= categoryEnabledMetrics.length) {
          return prev;
        }
        
        // Remove the dragged item and insert at new position
        const reorderedCategory = [...categoryEnabledMetrics];
        const [movedItem] = reorderedCategory.splice(sourceIndex, 1);
        reorderedCategory.splice(destinationIndex, 0, movedItem);
        
        // Create a map of new orders for the category
        const newOrderMap = new Map<string, number>();
        reorderedCategory.forEach((metric, idx) => {
          newOrderMap.set(metric.id, idx);
        });
        
        // Update all metrics with new orders
        const result = allMetrics.map(m => {
          if (newOrderMap.has(m.id)) {
            return { ...m, order: newOrderMap.get(m.id)! };
          }
          return m;
        });
        
        return result;
      }
      
      // Global reorder
      const newMetrics = [...prev].sort((a, b) => a.order - b.order);
      const [removed] = newMetrics.splice(sourceIndex, 1);
      newMetrics.splice(destinationIndex, 0, removed);
      
      return newMetrics.map((m, idx) => ({ ...m, order: idx }));
    });
    
    // Show confirmation toast
    toast.success("Orden guardado", {
      description: "La posición de las tarjetas se ha guardado correctamente",
      duration: 2000,
    });
  }, []);

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev);
  }, []);

  const resetToDefaults = () => {
    setMetrics(DEFAULT_METRICS);
    toast.success("Configuración restablecida", {
      description: "Las métricas han vuelto a su orden predeterminado",
    });
  };

  const isMetricEnabled = (metricId: string) => {
    return metrics.find(m => m.id === metricId)?.enabled ?? false;
  };

  const getMetricsByCategory = (category: MetricConfig["category"]) => {
    return metrics.filter(m => m.category === category).sort((a, b) => a.order - b.order);
  };

  const getOrderedEnabledMetrics = (category: MetricConfig["category"]) => {
    return metrics
      .filter(m => m.category === category && m.enabled)
      .sort((a, b) => a.order - b.order);
  };

  return {
    metrics,
    toggleMetric,
    setMetricEnabled,
    reorderMetrics,
    resetToDefaults,
    isMetricEnabled,
    getMetricsByCategory,
    getOrderedEnabledMetrics,
    isEditMode,
    toggleEditMode,
  };
};
